import { useState } from "react";
import { useTheme } from "@/hooks/useTheme";
import { useCategories } from "@/hooks/useCategories";
import { useChatConversation, type User } from "@/hooks/useChatConversation";
import { ChatProvider } from "@/contexts/ChatContext";
import { ChatInput } from "./chat/ChatInput";
import SynqusName from "@/components/SynqusName";
import {
  TimeSuggestionsCard,
  MeetingPrepCard,
  QuickReplies,
  AIBadge,
  TypingIndicator,
} from "./chat/AIResponseCards";
import type { NlpResult } from "@/types";
import { space, fontSize, fontWeight, radius, shadow, zIndex, transition, semanticColor, chatBubble } from "@/styles/tokens";

// ═══ Types ═══

interface ChatBubbleProps {
  users: User[];
  onAction: (result: NlpResult) => void;
  theme: string;
}

// ═══ ChatBubble Component ═══

export function ChatBubble({ users, onAction }: ChatBubbleProps) {
  const { colors: c } = useTheme();
  const categories = useCategories();
  const [open, setOpen] = useState(false);
  const {
    msgs,
    input,
    setInput,
    loading,
    loadingText,
    pendingResult,
    aiStatus,
    showSettings,
    setShowSettings,
    apiKeyInput,
    setApiKeyInput,
    configuring,
    scrollRef,
    send,
    pickPerson,
    pickCategory,
    pickSubcategory,
    pickDuration,
    addPerson,
    skipAddPeople,
    confirmSchedule,
    editSchedule,
    configureAI,
    addMessage,
    peopleSearch,
    setPeopleSearch,
  } = useChatConversation(users, onAction);

  // Wrapper to close chat after confirming schedule
  const handleConfirmSchedule = (): void => {
    confirmSchedule();
    setTimeout(() => setOpen(false), 800);
  };

  // ═══ Collapsed Button ═══

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        aria-label="Open Synqus AI chat"
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: chatBubble.fabSize,
          height: chatBubble.fabSize,
          borderRadius: radius.full,
          background: "linear-gradient(135deg,#7C3AED,#A78BFA)",
          color: semanticColor.white,
          border: "none",
          cursor: "pointer",
          boxShadow: shadow.md,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 24,
          zIndex: zIndex.overlay,
          transition: transition.normal,
          animation: "popIn 0.4s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.1)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        <span style={{ filter: "drop-shadow(0 0 6px rgba(255,255,255,0.5))" }}>&#x26A1;</span>
      </button>
    );
  }

  // ═══ Settings Panel ═══

  if (showSettings) {
    return (
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          width: chatBubble.settingsWidth,
          borderRadius: radius["4xl"],
          background: c.card,
          border: `1.5px solid ${c.bdr}`,
          boxShadow: shadow.lg,
          zIndex: zIndex.overlay,
          animation: "fadeUp 0.25s ease",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "14px 18px", background: `linear-gradient(135deg,${c.pri},#A78BFA)`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: semanticColor.white }}>AI Settings</div>
          <button onClick={() => setShowSettings(false)} style={{ background: "rgba(255,255,255,0.2)", border: "none", color: semanticColor.white, width: 28, height: 28, borderRadius: radius.full, cursor: "pointer", fontSize: fontSize.xl, display: "flex", alignItems: "center", justifyContent: "center" }}>&#x2715;</button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.txt, marginBottom: 4 }}>Status</div>
            <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
              <div style={{ width: 8, height: 8, borderRadius: radius.full, background: aiStatus?.enabled ? semanticColor.success : semanticColor.warning }} />
              <span style={{ fontSize: fontSize.md, color: c.tx2 }}>
                {aiStatus?.enabled ? `AI active — ${aiStatus.model}` : "Using local NLP (add API key to upgrade)"}
              </span>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.txt, marginBottom: 4 }}>Features</div>
            {[
              { label: "Smart NLP Parsing", enabled: aiStatus?.features?.smartParsing },
              { label: "Time Suggestions", enabled: aiStatus?.features?.timeSuggestions },
              { label: "Meeting Prep", enabled: aiStatus?.features?.meetingPrep },
              { label: "Conversational AI", enabled: aiStatus?.features?.conversational },
            ].map((f) => (
              <div key={f.label} style={{ display: "flex", alignItems: "center", gap: space[2], padding: "4px 0" }}>
                <div style={{ width: 6, height: 6, borderRadius: radius.full, background: f.enabled ? semanticColor.success : semanticColor.error }} />
                <span style={{ fontSize: fontSize.sm, color: c.tx2 }}>{f.label}</span>
              </div>
            ))}
          </div>

          {!aiStatus?.enabled && (
            <div>
              <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.txt, marginBottom: 4 }}>OpenAI API Key</div>
              <div style={{ fontSize: fontSize.xs, color: c.tx2, marginBottom: 8 }}>Enter your key to unlock AI-powered scheduling</div>
              <div style={{ display: "flex", gap: space[2] }}>
                <input
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-..."
                  type="password"
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: radius.lg,
                    border: `1px solid ${c.bdr}`,
                    background: c.s2,
                    color: c.txt,
                    fontSize: fontSize.md,
                    fontFamily: "inherit",
                    outline: "none",
                  }}
                  onKeyDown={(e) => { if (e.key === "Enter") configureAI(); }}
                />
                <button
                  onClick={configureAI}
                  disabled={configuring || !apiKeyInput.trim()}
                  style={{
                    padding: "8px 16px",
                    borderRadius: radius.lg,
                    background: c.pri,
                    color: semanticColor.white,
                    border: "none",
                    cursor: "pointer",
                    fontSize: fontSize.md,
                    fontWeight: fontWeight.bold,
                    opacity: configuring || !apiKeyInput.trim() ? 0.5 : 1,
                  }}
                >
                  {configuring ? "..." : "Activate"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ Open Chat Panel ═══

  return (
    <div
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        width: chatBubble.panelWidth,
        maxHeight: 580,
        borderRadius: radius["4xl"],
        background: c.card,
        border: `1.5px solid ${c.bdr}`,
        boxShadow: shadow.lg,
        display: "flex",
        flexDirection: "column",
        zIndex: zIndex.overlay,
        animation: "fadeUp 0.25s ease",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          background: `linear-gradient(135deg,${c.pri},#A78BFA)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: space[2] }}>
          <span style={{ fontSize: fontSize["3xl"], display: "inline-block", animation: "boltStrike 5s ease infinite", filter: "drop-shadow(0 0 4px rgba(255,255,255,0.4))" }}>&#x26A1;</span>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: space[1.5] }}>
              <span style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: semanticColor.white }}><SynqusName /> AI</span>
              <AIBadge enabled={aiStatus?.enabled || false} />
            </div>
            <div style={{ fontSize: fontSize.xs, color: "rgba(255,255,255,0.7)" }}>
              {aiStatus?.enabled ? "Powered by GPT" : "Smart scheduling assistant"}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: space[1] }}>
          <button
            onClick={() => setShowSettings(true)}
            aria-label="AI Settings"
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: semanticColor.white, width: 28, height: 28, borderRadius: radius.full, cursor: "pointer", fontSize: fontSize.base, display: "flex", alignItems: "center", justifyContent: "center" }}
            title="AI Settings"
          >
            &#x2699;
          </button>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close chat"
            style={{ background: "rgba(255,255,255,0.15)", border: "none", color: semanticColor.white, width: 28, height: 28, borderRadius: radius.full, cursor: "pointer", fontSize: fontSize.xl, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            &#x2715;
          </button>
        </div>
      </div>

      {/* Messages Thread + AI Cards */}
      <ChatProvider
        value={{
          messages: msgs,
          users,
          loading,
          pendingResult,
          peopleSearch,
          onPeopleSearchChange: setPeopleSearch,
          onPickPerson: pickPerson,
          onPickCategory: pickCategory,
          onPickSubcategory: pickSubcategory,
          onPickDuration: pickDuration,
          onAddPerson: addPerson,
          onSkipAddPeople: skipAddPeople,
          onConfirmSchedule: handleConfirmSchedule,
          onEditSchedule: editSchedule,
        }}
      >
        <div
          ref={scrollRef}
          role="log"
          aria-live="polite"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "14px",
            display: "flex",
            flexDirection: "column",
            gap: space[2.5],
            maxHeight: chatBubble.messagesMaxHeight,
          }}
        >
        {msgs.map((m, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
            {/* Message Bubble */}
            <div
              style={{
                maxWidth: chatBubble.maxWidth,
                padding: "10px 14px",
                borderRadius: m.role === "user" ? chatBubble.userRadius : chatBubble.botRadius,
                background: m.role === "user" ? c.pri : c.s2,
                color: m.role === "user" ? semanticColor.white : c.txt,
                fontSize: fontSize.base,
                lineHeight: 1.5,
              }}
            >
              {m.role === "bot" && m.parsed?.source === "ai" && (
                <span style={{ fontSize: fontSize["2xs"], background: `${semanticColor.aiPurple}25`, color: semanticColor.aiPurple, padding: "1px 5px", borderRadius: radius.xs, fontWeight: fontWeight.bold, marginRight: 6, verticalAlign: "middle" }}>AI</span>
              )}
              {m.text}
            </div>

            {/* AI Cards */}
            {m.aiCard === "time_suggestions" && i === msgs.length - 1 && (
              <TimeSuggestionsCard
                suggestions={m.aiData as Array<{ time: string; reason: string; score: number }>}
                onSelect={(time) => {
                  addMessage({ role: "user", text: `Let's go with ${new Date(time).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}` });
                }}
              />
            )}
            {m.aiCard === "meeting_prep" && (
              <MeetingPrepCard prep={m.aiData as { type: "ai" | "template"; content: string | null; meetingType: string; people: string[]; topic: string; agenda?: string[]; questions?: string[]; timeAllocation?: string }} />
            )}
            {m.aiCard === "quick_replies" && i === msgs.length - 1 && (
              <QuickReplies
                suggestions={m.aiData as string[]}
                onSelect={(text) => send(text)}
              />
            )}

            {/* Legacy NLP UI — disambiguation, category, subcategory, duration, people, confirm */}
            {m.parsed?.action === "disambiguate" && m.parsed.ambiguous && m.parsed.ambiguous.length > 0 && i === msgs.length - 1 && (
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: space[1] }}>
                {m.parsed.ambiguous[0].matches.map((person) => (
                  <button key={person.id} onClick={() => pickPerson(0, person)} style={{ padding: "8px 12px", borderRadius: radius.lg, border: `1px solid ${c.pri}44`, background: c.priG, color: c.pri, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.md, fontWeight: fontWeight.semibold, textAlign: "left", display: "flex", alignItems: "center", gap: space[2], transition: transition.fast }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = c.pri; e.currentTarget.style.color = semanticColor.white; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = c.priG; e.currentTarget.style.color = c.pri; }}
                  >
                    {person.displayName}
                  </button>
                ))}
              </div>
            )}

            {m.parsed?.action === "pick_category" && i === msgs.length - 1 && (
              <div style={{ marginTop: 8, display: "flex", gap: space[1], flexWrap: "wrap" }}>
                {categories.map((cat) => {
                  const isSuggested = m.parsed!.category === cat.id;
                  return (
                    <button key={cat.id} onClick={() => pickCategory(cat.id)} style={{ padding: "7px 12px", borderRadius: radius.lg, border: `1px solid ${isSuggested ? cat.color : cat.color + "44"}`, background: isSuggested ? `${cat.color}25` : `${cat.color}15`, color: cat.color, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.md, fontWeight: fontWeight.semibold, display: "flex", alignItems: "center", gap: 5, transition: transition.fast }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = cat.color; e.currentTarget.style.color = semanticColor.white; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = isSuggested ? `${cat.color}25` : `${cat.color}15`; e.currentTarget.style.color = cat.color; }}
                    >
                      {cat.icon} {cat.label}{isSuggested ? " \u2713" : ""}
                    </button>
                  );
                })}
              </div>
            )}

            {m.parsed?.action === "pick_subcategory" && i === msgs.length - 1 && (() => {
              const cat = categories.find((c2) => c2.id === m.parsed!.category);
              if (!cat) return null;
              return (
                <div style={{ marginTop: 8, display: "flex", gap: space[1], flexWrap: "wrap" }}>
                  {cat.subcategories.map((sub) => (
                    <button key={sub.id} onClick={() => pickSubcategory(sub.id)} style={{ padding: "6px 10px", borderRadius: radius.md + 1, border: `1px solid ${c.pri}44`, background: c.priG, color: c.pri, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.sm, fontWeight: fontWeight.semibold, display: "flex", alignItems: "center", gap: 4, transition: transition.fast }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = c.pri; e.currentTarget.style.color = semanticColor.white; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = c.priG; e.currentTarget.style.color = c.pri; }}
                    >
                      {sub.icon} {sub.label}
                    </button>
                  ))}
                </div>
              );
            })()}

            {m.parsed?.action === "pick_duration" && i === msgs.length - 1 && (
              <div style={{ marginTop: 8, display: "flex", gap: space[1], flexWrap: "wrap" }}>
                {[15, 30, 45, 60, 90].map((d) => (
                  <button key={d} onClick={() => pickDuration(d)} style={{ padding: "7px 14px", borderRadius: radius.lg, border: `1px solid ${m.parsed!.suggestedDur === d ? c.pri : c.bdr}`, background: m.parsed!.suggestedDur === d ? c.priG : "transparent", color: m.parsed!.suggestedDur === d ? c.pri : c.tx2, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.md, fontWeight: fontWeight.semibold, transition: transition.fast }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = c.pri; e.currentTarget.style.color = semanticColor.white; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = m.parsed!.suggestedDur === d ? c.priG : "transparent"; e.currentTarget.style.color = m.parsed!.suggestedDur === d ? c.pri : c.tx2; }}
                  >
                    {d} min{m.parsed!.suggestedDur === d ? " \u2713" : ""}
                  </button>
                ))}
              </div>
            )}

            {m.parsed?.action === "add_people" && i === msgs.length - 1 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: space[1], flexWrap: "wrap", marginBottom: space[2] }}>
                  {(pendingResult?._suggestions || []).filter((u) => !pendingResult?.people.includes(u.id)).map((u) => (
                    <button key={u.id} onClick={() => addPerson(u)} style={{ padding: "5px 10px", borderRadius: radius.lg, border: `1px solid ${c.pri}30`, background: c.priG, color: c.pri, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.sm, fontWeight: fontWeight.semibold, transition: transition.fast }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = c.pri; e.currentTarget.style.color = semanticColor.white; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = c.priG; e.currentTarget.style.color = c.pri; }}
                    >
                      + {u.displayName.split(" ")[0]}
                    </button>
                  ))}
                </div>
                <button onClick={skipAddPeople} style={{ padding: "6px 14px", borderRadius: radius.lg, background: c.pri, color: semanticColor.white, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.sm, fontWeight: fontWeight.bold }}>
                  Continue &#x2192;
                </button>
              </div>
            )}

            {/* Schedule Confirmation */}
            {m.parsed?.action === "schedule" && (
              <div style={{ marginTop: 6 }}>
                <div style={{ display: "flex", gap: space[1], flexWrap: "wrap", marginBottom: pendingResult && !m.parsed.confirmed ? space[2] : 0 }}>
                  {m.parsed.category && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: c.priG, color: c.pri, fontWeight: fontWeight.semibold }}>{m.parsed.category}</span>}
                  {m.parsed.subcategory && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: c.okBg, color: c.ok, fontWeight: fontWeight.semibold }}>{m.parsed.subcategory}</span>}
                  {m.parsed.people && m.parsed.people.length > 0 && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: c.warnBg, color: c.warn, fontWeight: fontWeight.semibold }}>{m.parsed.people.length} {m.parsed.people.length === 1 ? "person" : "people"}</span>}
                  {m.parsed.duration && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: `${c.pri}15`, color: c.pri, fontWeight: fontWeight.semibold }}>{m.parsed.duration}m</span>}
                  {m.parsed.title && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: `${semanticColor.aiPurple}15`, color: semanticColor.aiPurple, fontWeight: fontWeight.semibold }}>{m.parsed.title}</span>}
                  {m.parsed.timeHint && <span style={{ fontSize: fontSize.xs, padding: "2px 8px", borderRadius: radius.sm, background: `${semanticColor.teal}15`, color: semanticColor.teal, fontWeight: fontWeight.semibold }}>{m.parsed.timeHint}</span>}
                </div>
                {pendingResult && !m.parsed.confirmed && i === msgs.length - 1 && (
                  <div style={{ display: "flex", gap: space[1.5] }}>
                    <button onClick={handleConfirmSchedule} style={{ flex: 1, padding: "8px 12px", borderRadius: radius.lg, background: c.pri, color: semanticColor.white, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.md, fontWeight: fontWeight.bold, transition: transition.fast }}>
                      &#x2713; Find Slots
                    </button>
                    <button onClick={editSchedule} style={{ padding: "8px 12px", borderRadius: radius.lg, background: c.s2, color: c.tx2, border: `1px solid ${c.bdr}`, cursor: "pointer", fontFamily: "inherit", fontSize: fontSize.md, fontWeight: fontWeight.semibold }}>
                      Edit
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Loading Indicator */}
        {loading && <TypingIndicator text={loadingText} />}
        </div>
      </ChatProvider>

      {/* Input */}
      <ChatInput
        value={input}
        onChange={setInput}
        onSubmit={() => send()}
        disabled={loading}
        placeholder={aiStatus?.enabled ? "Ask anything about scheduling..." : "e.g. Book a standup with Sarah, 15 min"}
      />

      <style>{`
        @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes fadeUp { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
        @keyframes boltStrike { 0%, 100% { transform: rotate(0deg); } 25% { transform: rotate(-15deg); } 50% { transform: rotate(0deg); } 75% { transform: rotate(15deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes aiDotBounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-6px); } }
      `}</style>
    </div>
  );
}
